import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Home from './pages/Home';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import News from './pages/News';
import Admin from './pages/Admin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicProtocol from './pages/PublicProtocol';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import VerifyEmail from './pages/VerifyEmail';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/research" element={<Navigate to="/" replace />} />
      <Route path="/app" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/news" element={<News />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/p/:slug" element={<PublicProtocol />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppFooter() {
  return (
    <footer className="border-t border-gray-200 bg-white mt-8 py-4">
      <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs text-gray-400">
        <span>Protovera</span>
        <span>·</span>
        <a href="/privacy" className="underline hover:text-gray-600">Privacy</a>
        <span>·</span>
        <a href="/terms" className="underline hover:text-gray-600">Terms</a>
        <span>·</span>
        <span>
          Search results include data from{' '}
          <a href="https://pubmed.ncbi.nlm.nih.gov/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">PubMed</a>
          {' / '}
          <a href="https://www.ncbi.nlm.nih.gov/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">NCBI</a>
          ,{' '}
          <a href="https://arxiv.org/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">arXiv</a>
          ,{' '}
          <a href="https://openalex.org/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">OpenAlex</a>
          ,{' '}
          <a href="https://europepmc.org/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">Europe PMC</a>
          , and{' '}
          <a href="https://www.biorxiv.org/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">bioRxiv/medRxiv</a>
          .
        </span>
        <span>·</span>
        <a
          href="https://www.ncbi.nlm.nih.gov/About/disclaimer.html"
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-gray-600"
        >
          NCBI Disclaimer &amp; Copyright
        </a>
      </div>
    </footer>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-4">
          <p className="text-sm font-semibold text-[#171717] mb-2">Something went wrong</p>
          <p className="text-sm text-[#808080] mb-4">The error has been reported. Please refresh the page.</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm px-4 py-2 bg-[#171717] text-white rounded-md hover:bg-[#2e2e2e] transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    }>
    <BrowserRouter>
      <AuthProvider>
        <div className="flex flex-col min-h-screen">
          <div className="flex-1">
            <AppRoutes />
          </div>
          <AppFooter />
        </div>
      </AuthProvider>
    </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
